import { useState } from 'react';
import { SearchableSelect } from '@/shared/components';
import { useConfigOptionsComboboxQuery } from '../hooks/useConfigOptionsComboboxQuery';
import { isCampaignOnlyConfigGroup } from '../services/smr-admin';
import type { ConfigOptionRecord, ConfigOptionListParams } from '../services/smr-admin';

interface Props {
    value: ConfigOptionRecord | null;
    onChange: (next: ConfigOptionRecord | null) => void;
    placeholder?: string;
    excludeIds?: string[];
    disabled?: boolean;
    id?: string;
    /** Optional catalogue group to scope the search to (e.g. `policies` for the
     *  campaign Cancellation Rules tab). Omitted = search every group (default). */
    group?: NonNullable<ConfigOptionListParams['group']>;
}

const optionLabel = (o: ConfigOptionRecord): string =>
    o.label ? `${o.label} (${o.key_name})` : o.key_name;

/**
 * Catalog config-option search, built on the shared `SearchableSelect`. This wrapper owns
 * the async data source (`useConfigOptionsComboboxQuery`) and the `ConfigOptionRecord` shape;
 * SearchableSelect owns the search UX (debounce, keyboard nav, a11y). Async mode: the select
 * emits the settled search term, we query, and feed the results + loading state back.
 */
export function ConfigOptionCombobox({
    value,
    onChange,
    placeholder = 'Search catalog options…',
    excludeIds = [],
    disabled = false,
    id,
    group,
}: Props) {
    const [search, setSearch] = useState('');
    const { data, isFetching } = useConfigOptionsComboboxQuery({ search, page: 1, group });

    // Product-vs-campaign boundary: when the picker is unscoped (no explicit `group`,
    // i.e. the product-side `ConfigSettingAddForm`), drop campaign-only groups so they
    // can't be applied as product config settings. A campaign-scoped picker passes an
    // explicit `group` and is unaffected.
    const options = group
        ? (data?.data ?? [])
        : (data?.data ?? []).filter((o) => !isCampaignOnlyConfigGroup(o.group));

    return (
        <SearchableSelect<ConfigOptionRecord>
            id={id}
            options={options}
            value={value}
            onChange={onChange}
            onSearchChange={setSearch}
            loading={isFetching}
            getOptionValue={(o) => o.id}
            getOptionLabel={optionLabel}
            excludeValues={excludeIds}
            placeholder={placeholder}
            disabled={disabled}
            clearable
            emptyLabel="No options match."
        />
    );
}
